import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
} from 'react';
import { upperFirst } from 'lodash';
import { TranslationUpdater } from '../../../context/util/translationHelper';
import { IUseListFilterState, useListFilterState } from './useListFilterState';

const searchListFilterContext = createContext<IUseListFilterState>(
  {} as IUseListFilterState
);

/** Generalized context base for creating filter contexts for lists. */
export const ListFilterProvider = ({
  children,
  state,
}: {
  children: ReactNode;
  state?: IUseListFilterState;
}) => {
  const defaultState = useListFilterState({
    tableName: 'default',
  });
  const value = state || defaultState;

  return (
    <searchListFilterContext.Provider value={value}>
      <TranslationUpdater onLanguageUpdate={state?.onInteract} />
      {children}
    </searchListFilterContext.Provider>
  );
};

/** Get an individual filter by its key (copies the syntax of useState)
 *
 * @example
 * const [impact, setImpact]=useFilterState('impact');
 * @param key what is the filter we're looking for?
 * @template T the type to override
 */
export const useFilterState = <T extends unknown>(
  key: any
): [T, Dispatch<SetStateAction<T>>] => {
  const { filterStateList } = useFilterData();

  return [filterStateList[key], filterStateList[makeDispatchNameFrom(key)]];
};

export const useFilterData = (): IUseListFilterState => {
  const value = useContext(searchListFilterContext);

  if (value === undefined) {
    throw new Error('useFilterData must be used within a ListFilterProvider');
  }
  return value;
};
/** Makes a string name for a function from the name of it's value.
 * @example
 * makeFunctionName('testValue') // setTestValue
 */
export const makeDispatchNameFrom = (key: string): string =>
  `set${upperFirst(key)}`;

export const SearchFilterConsumer = searchListFilterContext.Consumer;

export type FilterData = { [key: string]: any };

export interface IUseFilterResult extends IUseListFilterState {}
